import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Layers, Package, ClipboardList, Factory, Plus, Network, AlertTriangle, CheckCircle2, FolderTree } from 'lucide-react';
import { PageHeader, KpiCard, STREAMS } from '../components/PageHeader';
import { AssembliesToBuildPanel } from '../components/AssembliesToBuildPanel';

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const RecentBuilds = ({ rows }) => {
  const navigate = useNavigate();
  return (
    <Card className="industrial-card p-4" data-testid="stream-recent-builds">
      <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" />Recently completed builds</h2>
      {rows.length === 0 ? <p className="text-xs text-slate-500">No builds completed in the last 8 weeks.</p> : rows.map(r => (
        <button key={r.id} onClick={() => navigate(`/work-orders/${r.id}`)} className="w-full text-left flex justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0 text-sm hover:text-orange-600" data-testid={`stream-recent-${r.id}`}>
          <span className="truncate">{r.name}</span><span className="text-xs mono text-slate-500 shrink-0">{r.work_order_number || r.id.slice(0, 8)} · {(r.completed_at || '').slice(0, 10)}</span>
        </button>
      ))}
    </Card>
  );
};

const BuildsChart = ({ data, stream }) => (
  <Card className="industrial-card p-4" data-testid="stream-builds-chart">
    <h2 className="text-sm font-semibold text-slate-700 mb-2">Completed builds per week</h2>
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
          <Tooltip />
          <Bar dataKey="completed" name="Completed" fill={stream === 'assembly' ? '#0284c7' : '#ea580c'} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

const QuickLinks = ({ s, level }) => {
  const navigate = useNavigate();
  const links = [
    [`${s.base}/catalog`, level === 'assembly' ? Layers : Package, `${s.singular} catalog`],
    [`${s.base}/boms`, ClipboardList, `${s.singular} BOMs`],
    [`${s.base}/builds`, Factory, `${s.singular} build queue`],
    ...(level === 'product' ? [['/products/families', FolderTree, 'Families']] : []),
    ['/structure', Network, 'Product structure'],
  ];
  return (
    <Card className="industrial-card p-4 space-y-1" data-testid="stream-quick-links">
      <h2 className="text-sm font-semibold text-slate-700 mb-1">Go to</h2>
      {links.map(([to, Icon, label]) => (
        <button key={to} onClick={() => navigate(to)} className="w-full flex items-center gap-2 text-sm text-slate-700 px-2 py-1.5 rounded-sm hover:bg-slate-50 transition-colors" data-testid={`stream-link-${to.replace(/\//g, '-')}`}>
          <Icon className="w-4 h-4 text-slate-400" />{label}
        </button>
      ))}
    </Card>
  );
};

export default function StreamOverviewPage({ level }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const s = STREAMS[level];
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api().get(`/streams/${level}/summary`).then(r => setD(r.data)).catch(() => setD(null)); }, [api, level]);
  const k = d?.kpis || {};
  const isAsm = level === 'assembly';

  return (
    <div className="space-y-6 animate-slideIn" data-testid={`stream-overview-${level}`}>
      <PageHeader
        crumbs={[{ label: 'Build' }, { label: s.label }]}
        title={`${s.label} overview`} icon={isAsm ? Layers : Package} stream={level} badge={`${s.singular} stream`}
        description={isAsm
          ? 'Sub-assemblies built from parts (and other assemblies), stocked, then consumed by product builds.'
          : 'Finished products made from assemblies and loose parts, grouped into families, built to order and shipped.'}
        actions={<>
          <Button variant="outline" className="rounded-sm" onClick={() => navigate(`${s.base}/catalog`)} data-testid="stream-new-catalog-btn"><Plus className="w-4 h-4 mr-1" />New {s.singular.toLowerCase()}</Button>
          <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={() => navigate(`${s.base}/builds`)} data-testid="stream-build-queue-btn"><Factory className="w-4 h-4 mr-1" />Build queue</Button>
        </>}
        kpis={<>
          <KpiCard stream={level} label={isAsm ? 'Assemblies' : 'Products'} value={k.catalog ?? '—'} sub={k.families != null ? `${k.families} families` : null} testId="stream-kpi-catalog" />
          <KpiCard stream={level} label="Released BOMs" value={k.boms?.Released ?? '—'} sub={`${k.boms?.Draft ?? 0} draft`} testId="stream-kpi-boms" />
          <KpiCard stream={level} label="Open builds" value={k.open_builds ?? '—'} testId="stream-kpi-open" />
          <KpiCard stream={level} label="Completed (30d)" value={k.completed_30d ?? '—'} testId="stream-kpi-completed" />
          <KpiCard stream={level} label="In stock" value={k.stock?.units_on_hand ?? '—'} sub={isAsm ? `${money(k.stock?.value)} material value` : `${k.stock?.shipped ?? 0} shipped`} testId="stream-kpi-stock" />
        </>}
      />
      {!d ? <div className="flex items-center justify-center h-40"><div className="spinner" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <BuildsChart data={d.builds_per_week} stream={level} />
            {isAsm ? <AssembliesToBuildPanel /> : <RecentBuilds rows={d.recent_builds} />}
          </div>
          <div className="lg:col-span-4 space-y-6">
            <QuickLinks s={s} level={level} />
            {isAsm ? <RecentBuilds rows={d.recent_builds} /> : (
              <Card className={`p-4 border ${s.soft}`} data-testid="stream-product-note">
                <p className="text-xs flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" />Product builds automatically create assembly builds for any assembly short in stock. Watch the Assemblies stream for those.</p>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
